import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

const MotionBox = motion(Box);

const BRAND = {
  turquoise: "#00B4A6",
  turquoiseLight: "#5FF5EA",
  turquoiseDark: "#00796B",
  turquoiseSoft: "#DDFBF8",
  gold: "#D4AF37",
  goldLight: "#FFE08A",
  ink: "#071313",
  deep: "#031817",
  muted: "#637D7A",
  border: "#D8EFEB",
  card: "#FFFFFF",
  subtle: "#F3FFFC",
};

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'JetBrains Mono', 'Fira Code', monospace";

const OPPORTUNITIES_ENDPOINT = "/api/opportunities-feed/";
const SUBSCRIPTION_ENDPOINT = "/api/opportunity-subscriptions/";

const SITE_NAME = "Art For My Room";
const SITE_URL = "https://artformyroom.com";
const SITE_EMAIL = "info@artformyroom.com";

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1400&q=80";

const countryOptions = [
  { value: "GB", label: "United Kingdom", short: "UK" },
  { value: "EN", label: "England", short: "England" },
  { value: "SC", label: "Scotland", short: "Scotland" },
  { value: "WA", label: "Wales", short: "Wales" },
  { value: "NI", label: "Northern Ireland", short: "NI" },
  { value: "EU", label: "Europe / Creative Europe", short: "Europe" },
  { value: "US", label: "United States", short: "US" },
  { value: "CA", label: "Canada", short: "Canada" },
  { value: "AU", label: "Australia", short: "Australia" },
  { value: "GLOBAL", label: "Worldwide", short: "Global" },
];

const typeOptions = [
  "All",
  "Grants",
  "Open Calls",
  "Residencies",
  "Commissions",
  "Public Art",
  "Travel & Mobility",
  "Creative Business",
  "Exhibitions",
  "Directories",
];

const sortOptions = [
  { value: "recommended", label: "Recommended first" },
  { value: "deadline", label: "Deadline soon" },
  { value: "newest", label: "Newest first" },
  { value: "amount", label: "Funding value" },
];

const practiceOptions = [
  "Painting",
  "Visual arts",
  "Photography",
  "Illustration",
  "Public art",
  "Mixed media",
  "Digital art",
  "Craft",
  "Creative business",
  "Curating",
];

const curatedOpportunitySources = [
  {
    id: "ace-project-grants",
    title: "Arts Council England National Lottery Project Grants",
    source: "Arts Council England",
    countryTags: ["GB", "EN"],
    type: "Grants",
    amount: "£1,000 to £100,000",
    deadline: "Open programme",
    cadence: "Open access",
    audience: "Artists, creatives, museums, libraries and cultural projects in England",
    bestFor:
      "Painting projects, exhibitions, creative workshops, research, public engagement and artist development.",
    whyUseful:
      "One of the strongest UK funding routes for individual artists and small creative projects in England.",
    applicationTip:
      "Prepare a clear public benefit, budget, timeline, partners and evidence that the work reaches people.",
    url: "https://www.artscouncil.org.uk/ProjectGrants",
    score: 99,
    image:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80",
    keywords: ["arts council", "project grants", "england", "national lottery"],
  },
  {
    id: "creative-scotland-open-fund",
    title: "Creative Scotland Open Fund for Individuals",
    source: "Creative Scotland",
    countryTags: ["GB", "SC"],
    type: "Grants",
    amount: "Check current guidance",
    deadline: "Open fund",
    cadence: "Rolling or programme-based",
    audience: "Freelance and self-employed artists and creative practitioners in Scotland",
    bestFor:
      "Developing artistic practice, creating new work, research, testing ideas and reaching audiences.",
    whyUseful:
      "A Scotland-specific route for individual artists who need support for a creative project or development period.",
    applicationTip:
      "Explain the artistic idea, why now, who benefits and what changes after the funding.",
    url: "https://www.creativescotland.com/funding/funding-programmes/open-funding/open-fund-for-individuals",
    score: 96,
    image:
      "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1400&q=80",
    keywords: ["scotland", "creative scotland", "open fund", "individual artists"],
  },
  {
    id: "arts-wales-individuals",
    title: "Arts Council of Wales Funding for Individuals",
    source: "Arts Council of Wales",
    countryTags: ["GB", "WA"],
    type: "Grants",
    amount: "Small and large lottery-funded grants",
    deadline: "Check current funds",
    cadence: "Programme-based",
    audience: "Creative individuals in Wales",
    bestFor:
      "Developing practice, making new work, international development and professional growth.",
    whyUseful:
      "A practical first stop for Wales-based artists looking for official arts funding.",
    applicationTip:
      "Check eligibility first, then match your project to the exact fund rather than forcing one generic application.",
    url: "https://arts.wales/funding/individuals",
    score: 94,
    image:
      "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1400&q=80",
    keywords: ["wales", "arts council wales", "artists", "funding"],
  },
  {
    id: "arts-council-ni-siap",
    title: "Arts Council Northern Ireland Support for Individual Artists",
    source: "Arts Council Northern Ireland",
    countryTags: ["GB", "NI"],
    type: "Grants",
    amount: "Often £62 to £15,000 depending on scheme",
    deadline: "Usually programme windows",
    cadence: "Annual and rolling programmes",
    audience: "Individual artists in Northern Ireland",
    bestFor:
      "Practice development, research, mentoring, training, travel and creating new work.",
    whyUseful:
      "The core official route for individual artist support in Northern Ireland.",
    applicationTip:
      "Watch annual SIAP windows and rolling travel award opportunities, then prepare evidence of practice early.",
    url: "https://artscouncil-ni.org/funding/funding-for-individuals",
    score: 93,
    image:
      "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1400&q=80",
    keywords: ["northern ireland", "siap", "individual artists", "travel awards"],
  },
  {
    id: "british-council-international-collaboration",
    title: "British Council International Collaboration Grants",
    source: "British Council Arts",
    countryTags: ["GB", "GLOBAL"],
    type: "Travel & Mobility",
    amount: "Programme-based",
    deadline: "Check current round",
    cadence: "Periodic calls",
    audience: "UK artists, arts professionals, organisations and international partners",
    bestFor:
      "Cross-border cultural projects, international partnerships, exhibitions and collaborative development.",
    whyUseful:
      "Useful when a UK artist wants a credible international collaboration route rather than a local-only grant.",
    applicationTip:
      "Start with a partner, a clear shared outcome and a project that makes sense in both countries.",
    url: "https://arts.britishcouncil.org/projects/international-collaboration-grants",
    score: 91,
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80",
    keywords: ["british council", "international collaboration", "global artists"],
  },
  {
    id: "innovate-uk-creative-industries",
    title: "Innovate UK Business Connect Creative Industries",
    source: "Innovate UK Business Connect",
    countryTags: ["GB"],
    type: "Creative Business",
    amount: "Varies by competition",
    deadline: "Ongoing competitions and support",
    cadence: "Rolling opportunities",
    audience: "Creative businesses, creative-tech founders and innovation teams",
    bestFor:
      "Artists building digital tools, AI art platforms, creative marketplaces, immersive experiences or creative-tech products.",
    whyUseful:
      "Good for Art For My Room-style creative technology, especially when the project has innovation and growth potential.",
    applicationTip:
      "Frame the project as a scalable creative-industry innovation, not only as an art website.",
    url: "https://iuk-business-connect.org.uk/creative-industries/",
    score: 90,
    image:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1400&q=80",
    keywords: ["creative industries", "innovation", "funding", "business support"],
  },
  {
    id: "culture-moves-europe",
    title: "Culture Moves Europe",
    source: "European Union",
    countryTags: ["EU", "GLOBAL"],
    type: "Travel & Mobility",
    amount: "Mobility and residency grants",
    deadline: "Call-based",
    cadence: "Calls from 2025 to 2028",
    audience: "Artists and cultural professionals across Creative Europe countries",
    bestFor:
      "Travel, international projects, residencies, research trips, collaboration and cultural mobility.",
    whyUseful:
      "One of the strongest European mobility routes for artists who want to work across borders.",
    applicationTip:
      "Build the application around a host, route, purpose, artistic result and practical travel plan.",
    url: "https://culture.ec.europa.eu/culture-moves-europe",
    score: 97,
    image:
      "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1400&q=80",
    keywords: ["creative europe", "mobility", "residency", "travel grant"],
  },
  {
    id: "eu-culture-calls",
    title: "EU Culture and Creativity Funding Calls",
    source: "European Commission",
    countryTags: ["EU"],
    type: "Open Calls",
    amount: "Varies by call",
    deadline: "Call-based",
    cadence: "Ongoing call listings",
    audience: "Creative and cultural sector applicants across Europe",
    bestFor:
      "European cultural projects, cooperation, platforms, networks and sector-level creative projects.",
    whyUseful:
      "The official place to monitor EU cultural funding calls, especially for collaborative applications.",
    applicationTip:
      "Look for partners early. Many EU opportunities are stronger when the project is clearly international.",
    url: "https://culture.ec.europa.eu/funding/calls",
    score: 94,
    image:
      "https://images.unsplash.com/photo-1526470608268-f674ce90ebd4?auto=format&fit=crop&w=1400&q=80",
    keywords: ["eu", "creative europe", "calls", "cultural funding"],
  },
  {
    id: "eu-funding-tenders-creative-europe",
    title: "Creative Europe on the EU Funding & Tenders Portal",
    source: "EU Funding & Tenders Portal",
    countryTags: ["EU"],
    type: "Open Calls",
    amount: "Varies by call",
    deadline: "Call-based",
    cadence: "Official calls",
    audience: "Creative Europe applicants and cultural organisations",
    bestFor:
      "Formal EU proposals, larger collaborations, calls with structured eligibility and official documents.",
    whyUseful:
      "The formal EU application portal for Creative Europe calls.",
    applicationTip:
      "Read eligibility, partner rules and portal documents before drafting. EU applications dislike vague fog.",
    url: "https://ec.europa.eu/info/funding-tenders/opportunities/portal/screen/programmes/crea",
    score: 92,
    image:
      "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1400&q=80",
    keywords: ["eu portal", "creative europe", "funding tenders"],
  },
  {
    id: "artquest-opportunities",
    title: "Artquest Opportunities",
    source: "Artquest",
    countryTags: ["GB", "GLOBAL"],
    type: "Directories",
    amount: "Varies",
    deadline: "Updated listings",
    cadence: "Listings",
    audience: "Artists looking for high-quality open calls and professional development",
    bestFor:
      "Open calls, commissions, exhibitions, residencies, advice and artist career support.",
    whyUseful:
      "A strong UK-facing artist resource for opportunities and practical career guidance.",
    applicationTip:
      "Use it as a weekly habit. Save calls that match your practice, not every shiny thing.",
    url: "https://artquest.org.uk/",
    score: 89,
    image:
      "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1400&q=80",
    keywords: ["artquest", "artist opportunities", "open calls"],
  },
  {
    id: "artrabbit-opportunities",
    title: "ArtRabbit Artist Opportunities",
    source: "ArtRabbit",
    countryTags: ["GB", "GLOBAL"],
    type: "Open Calls",
    amount: "Varies",
    deadline: "Closing-soon listings",
    cadence: "Listings",
    audience: "Contemporary artists, writers and curators",
    bestFor:
      "Competitions, awards, exhibition opportunities, proposals and grants.",
    whyUseful:
      "Good for scanning live calls and seeing what is closing soon.",
    applicationTip:
      "Filter by deadline and only apply where the theme fits your actual work.",
    url: "https://www.artrabbit.com/artist-opportunities",
    score: 86,
    image:
      "https://images.unsplash.com/photo-1518998053901-5348d3961a04?auto=format&fit=crop&w=1400&q=80",
    keywords: ["open calls", "exhibition", "grants", "awards"],
  },
  {
    id: "on-the-move-open-calls",
    title: "On the Move Open Calls by Country",
    source: "On the Move",
    countryTags: ["EU", "GLOBAL"],
    type: "Travel & Mobility",
    amount: "Usually supported mobility",
    deadline: "Country-based calls",
    cadence: "Updated listings",
    audience: "Artists and cultural professionals wanting to work internationally",
    bestFor:
      "Residencies, travel-supported calls, creative training and international work.",
    whyUseful:
      "Excellent when the artist wants mobility opportunities by country of destination.",
    applicationTip:
      "Look for calls where travel or accommodation is covered, not just exposure.",
    url: "https://on-the-move.org/news/countries",
    score: 95,
    image:
      "https://images.unsplash.com/photo-1473959383416-9bb17f103a3e?auto=format&fit=crop&w=1400&q=80",
    keywords: ["mobility", "international", "residencies", "open calls"],
  },
  {
    id: "res-artis-open-calls",
    title: "Res Artis Open Calls",
    source: "Res Artis",
    countryTags: ["GLOBAL"],
    type: "Residencies",
    amount: "Varies by residency",
    deadline: "Current open calls",
    cadence: "Updated listings",
    audience: "Artists looking for residencies worldwide",
    bestFor:
      "Studio time, international residencies, research, cultural exchange and focused creation periods.",
    whyUseful:
      "One of the clearest global residency discovery networks.",
    applicationTip:
      "Check fees, accommodation, travel support and studio access before applying.",
    url: "https://resartis.org/open-calls/",
    score: 93,
    image:
      "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1400&q=80",
    keywords: ["residencies", "open calls", "global artists"],
  },
  {
    id: "creative-capital-artist-opportunities",
    title: "Creative Capital Artist Opportunities",
    source: "Creative Capital",
    countryTags: ["US", "GLOBAL"],
    type: "Directories",
    amount: "Varies",
    deadline: "Updated listings",
    cadence: "Listings",
    audience: "Artists looking for grants, awards and residencies",
    bestFor:
      "US and international opportunity discovery, especially grants and residency scanning.",
    whyUseful:
      "A broad artist opportunity list with a professional development angle.",
    applicationTip:
      "Use it to build a monthly application calendar rather than a last-minute panic festival.",
    url: "https://creative-capital.org/artist-resources/artist-opportunities/",
    score: 88,
    image:
      "https://images.unsplash.com/photo-1520975682031-a26dcbc53712?auto=format&fit=crop&w=1400&q=80",
    keywords: ["creative capital", "grants", "residencies", "artist resources"],
  },
  {
    id: "nea-grants",
    title: "National Endowment for the Arts Grants",
    source: "National Endowment for the Arts",
    countryTags: ["US"],
    type: "Grants",
    amount: "Varies by programme",
    deadline: "Programme-based",
    cadence: "Official grant cycles",
    audience: "US arts organisations, state arts agencies, regional arts organisations and eligible projects",
    bestFor:
      "Larger arts projects, community arts, public benefit, organisations and partnership-led work.",
    whyUseful:
      "The official federal arts funding hub for the United States.",
    applicationTip:
      "For individual artists, check eligibility carefully and look for partner organisations when needed.",
    url: "https://www.arts.gov/grants",
    score: 90,
    image:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80",
    keywords: ["nea", "united states", "arts grants"],
  },
  {
    id: "canada-council-grants",
    title: "Canada Council for the Arts Grants",
    source: "Canada Council for the Arts",
    countryTags: ["CA"],
    type: "Grants",
    amount: "Varies by programme",
    deadline: "Programme-based",
    cadence: "Official programmes",
    audience: "Canadian artists, arts workers, groups and arts organisations",
    bestFor:
      "Creation, development, professional growth, sharing work and artistic practice support.",
    whyUseful:
      "The central national arts funder for Canadian artists and arts organisations.",
    applicationTip:
      "Choose the programme first, then write the project around the specific outcome that programme supports.",
    url: "https://canadacouncil.ca/funding/grants",
    score: 94,
    image:
      "https://images.unsplash.com/photo-1519834022362-d4e81d12d3f8?auto=format&fit=crop&w=1400&q=80",
    keywords: ["canada", "canada council", "artist grants"],
  },
  {
    id: "creative-australia-opportunities",
    title: "Creative Australia Grants & Opportunities",
    source: "Creative Australia",
    countryTags: ["AU"],
    type: "Grants",
    amount: "Varies by programme",
    deadline: "Current opportunities",
    cadence: "Official listings",
    audience: "Australian artists, groups and creative organisations",
    bestFor:
      "Grants, residencies, scholarships, awards and visual arts opportunities in Australia.",
    whyUseful:
      "The main official discovery hub for Australian creative grants and opportunities.",
    applicationTip:
      "Use the eligibility filters and read the assessment criteria before drafting your story.",
    url: "https://creative.gov.au/investments-opportunities",
    score: 94,
    image:
      "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1400&q=80",
    keywords: ["australia", "creative australia", "grants", "visual arts"],
  },
  {
    id: "creative-australia-arts-projects-individuals",
    title: "Creative Australia Arts Projects for Individuals and Groups",
    source: "Creative Australia",
    countryTags: ["AU"],
    type: "Grants",
    amount: "$10,000 to $50,000",
    deadline: "Check current round",
    cadence: "Programme-based",
    audience: "Australian individual artists and groups",
    bestFor:
      "Creation, development, presentation and national or international arts projects.",
    whyUseful:
      "A practical visual-arts-friendly route for Australian artists needing project funding.",
    applicationTip:
      "Make the artistic outcome, audience, timeline and budget extremely clear.",
    url: "https://creative.gov.au/investments-opportunities/arts-projects-individuals-and-groups",
    score: 92,
    image:
      "https://images.unsplash.com/photo-1506784365847-bbad939e9335?auto=format&fit=crop&w=1400&q=80",
    keywords: ["creative australia", "individual artists", "project funding"],
  },
];

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function normaliseArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/[,|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function forceHttps(url) {
  if (!url) return "";
  const clean = String(url).trim();

  if (clean.startsWith("http://")) {
    return `https://${clean.slice("http://".length)}`;
  }

  return clean;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function parseDate(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function formatDate(value) {
  const date = parseDate(value);

  if (!date) return "Check source";

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  const date = parseDate(value);

  if (!date) return null;

  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysUntil(value) {
  const date = parseDate(value);

  if (!date) return null;

  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const targetMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  return Math.round((targetMidnight.getTime() - todayMidnight.getTime()) / 86400000);
}

function getDeadlineLabel(item) {
  const days = daysUntil(item.deadlineDate);

  if (days === null) return item.deadline || "Check source";
  if (days < 0) return "Deadline passed";
  if (days === 0) return "Closes today";
  if (days === 1) return "Closes tomorrow";
  if (days <= 14) return `Closes in ${days} days`;

  return formatDate(item.deadlineDate);
}

function normaliseUrl(value) {
  if (!value) return "";

  try {
    const url = new URL(value);
    return `${url.hostname.replace(/^www\./, "")}${url.pathname}`
      .replace(/\/+$/, "")
      .toLowerCase();
  } catch {
    return String(value).toLowerCase().trim();
  }
}

function slugify(value) {
  return safeText(value)
    .toLowerCase()
    .replace(/https?:\/\//g, "")
    .replace(/www\./g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "artist-opportunity";
}

function getCountryLabel(value) {
  const match = countryOptions.find((country) => country.value === value);
  return match ? match.label : value;
}

function getCountryShort(value) {
  const match = countryOptions.find((country) => country.value === value);
  return match ? match.short : value;
}

function getTypeIcon(type) {
  const value = safeText(type).toLowerCase();

  if (value.includes("grant")) return <PaidOutlinedIcon />;
  if (value.includes("resid")) return <HomeWorkOutlinedIcon />;
  if (value.includes("travel") || value.includes("mobility")) return <TravelExploreOutlinedIcon />;
  if (value.includes("commission") || value.includes("public")) return <BrushOutlinedIcon />;
  if (value.includes("business")) return <SellOutlinedIcon />;
  if (value.includes("exhibition")) return <PaletteOutlinedIcon />;
  if (value.includes("director")) return <LibraryBooksRoundedIcon />;

  return <ArticleOutlinedIcon />;
}

function countryMatches(item, selectedCountry) {
  const countryTags = normaliseArray(item.countryTags || item.country_tags || item.countries);

  if (!selectedCountry) return true;

  if (selectedCountry === "GLOBAL") {
    return countryTags.includes("GLOBAL") || countryTags.length === 0;
  }

  if (selectedCountry === "GB") {
    return countryTags.some((tag) => ["GB", "UK", "EN", "SC", "WA", "NI", "GLOBAL"].includes(tag));
  }

  if (selectedCountry === "EU") {
    return countryTags.some((tag) => ["EU", "GLOBAL"].includes(tag));
  }

  return countryTags.includes(selectedCountry) || countryTags.includes("GLOBAL");
}

function typeMatches(item, selectedType) {
  if (!selectedType || selectedType === "All") return true;

  const itemType = safeText(item.type).toLowerCase();
  const queryType = selectedType.toLowerCase();

  return itemType.includes(queryType.replace(" & ", " ")) || queryType.includes(itemType);
}

function searchMatches(item, query) {
  const q = safeText(query).toLowerCase();

  if (!q) return true;

  const text = [
    item.title,
    item.source,
    item.type,
    item.amount,
    item.deadline,
    item.audience,
    item.bestFor,
    item.whyUseful,
    item.applicationTip,
    normaliseArray(item.keywords).join(" "),
    normaliseArray(item.countryTags).join(" "),
  ]
    .join(" ")
    .toLowerCase();

  return text.includes(q);
}

function normaliseOpportunity(rawItem, index = 0) {
  const title =
    rawItem?.title ||
    rawItem?.name ||
    rawItem?.headline ||
    rawItem?.opportunity_title ||
    `Artist opportunity ${index + 1}`;

  const source =
    rawItem?.source ||
    rawItem?.publisher ||
    rawItem?.provider ||
    rawItem?.organisation ||
    rawItem?.organization ||
    "Opportunity source";

  const type =
    rawItem?.type ||
    rawItem?.category ||
    rawItem?.opportunity_type ||
    rawItem?.opportunityType ||
    "Open Calls";

  const countryTags = normaliseArray(
    rawItem?.countryTags ||
      rawItem?.country_tags ||
      rawItem?.countries ||
      rawItem?.country ||
      rawItem?.country_code
  );

  const amount =
    rawItem?.amount ||
    rawItem?.funding ||
    rawItem?.value ||
    rawItem?.grant_amount ||
    rawItem?.award ||
    "Check source";

  const deadlineDate =
    rawItem?.deadlineDate ||
    rawItem?.deadline_date ||
    rawItem?.deadline ||
    rawItem?.closing_date ||
    "";

  const url =
    rawItem?.url ||
    rawItem?.link ||
    rawItem?.sourceUrl ||
    rawItem?.source_url ||
    rawItem?.apply_url ||
    "";

  return {
    id: rawItem?.id || slugify(`${title}-${source}-${index}`),
    title,
    source,
    countryTags: countryTags.length ? countryTags : ["GLOBAL"],
    type,
    amount,
    deadline: rawItem?.deadline_label || rawItem?.deadlineText || rawItem?.deadline || "Check source",
    deadlineDate,
    cadence: rawItem?.cadence || rawItem?.frequency || "Live listing",
    audience:
      rawItem?.audience ||
      rawItem?.eligibility ||
      rawItem?.who_can_apply ||
      "Artists and creative practitioners",
    bestFor:
      rawItem?.bestFor ||
      rawItem?.best_for ||
      rawItem?.summary ||
      rawItem?.description ||
      "A useful opportunity for artists to explore.",
    whyUseful:
      rawItem?.whyUseful ||
      rawItem?.why_useful ||
      rawItem?.artist_note ||
      "Useful for artists looking for funding, visibility, development or new routes to show work.",
    applicationTip:
      rawItem?.applicationTip ||
      rawItem?.application_tip ||
      rawItem?.next_step ||
      "Read the source carefully, check eligibility and prepare your materials before applying.",
    url: forceHttps(url),
    image: forceHttps(rawItem?.image || rawItem?.image_url || rawItem?.thumbnail || DEFAULT_IMAGE),
    score: Math.max(1, Math.min(100, Number(rawItem?.score || rawItem?.priority || 80))),
    keywords: normaliseArray(rawItem?.keywords || rawItem?.tags),
    isLive: true,
    createdAt: rawItem?.created_at || rawItem?.createdAt || rawItem?.published_at || "",
  };
}

function dedupeOpportunities(items) {
  const map = new Map();

  items.forEach((item) => {
    const key = normaliseUrl(item.url) || `${item.source}-${item.title}`.toLowerCase();

    if (!map.has(key)) {
      map.set(key, item);
      return;
    }

    const existing = map.get(key);
    if ((item.score || 0) > (existing.score || 0)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
}

function sortOpportunities(items, sortBy) {
  const copy = [...items];

  if (sortBy === "deadline") {
    return copy.sort((a, b) => {
      const aDays = daysUntil(a.deadlineDate);
      const bDays = daysUntil(b.deadlineDate);

      if (aDays === null && bDays === null) return (b.score || 0) - (a.score || 0);
      if (aDays === null) return 1;
      if (bDays === null) return -1;

      return aDays - bDays;
    });
  }

  if (sortBy === "newest") {
    return copy.sort((a, b) => {
      const aTime = parseDate(a.createdAt)?.getTime() || 0;
      const bTime = parseDate(b.createdAt)?.getTime() || 0;

      return bTime - aTime;
    });
  }

  if (sortBy === "amount") {
    return copy.sort((a, b) => {
      const extract = (value) => {
        const numbers = safeText(value)
          .replace(/,/g, "")
          .match(/\d+/g);

        if (!numbers) return 0;

        return Math.max(...numbers.map((number) => Number(number) || 0));
      };

      return extract(b.amount) - extract(a.amount);
    });
  }

  return copy.sort((a, b) => (b.score || 0) - (a.score || 0));
}

function updateMetaTag(name, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[name="${name}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function updatePropertyTag(property, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[property="${property}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function updateCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", url);
}

function updateJsonLd(countryLabel, opportunities) {
  const id = "art-opportunities-jsonld";
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `Art opportunities for ${countryLabel}`,
      description:
        "A curated Art For My Room page helping painters and visual artists discover grants, open calls, residencies, commissions, public art routes and creative business support.",
      url: `${SITE_URL}/creative-tech-opportunities`,
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
      },
      about: opportunities.slice(0, 12).map((item) => ({
        "@type": "CreativeWork",
        name: item.title,
        url: item.url,
        provider: item.source,
      })),
    },
    null,
    2
  );
}

const fall = keyframes`
  0% { transform: translateY(-12px); opacity: 0; }
  10% { opacity: 0.55; }
  100% { transform: translateY(120vh); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(1deg); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-130%); }
  100% { transform: translateX(130%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.72; transform: scale(1.04); }
`;

const PageShell = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 10% 8%, ${alpha(BRAND.turquoise, 0.20)}, transparent 32%),
     radial-gradient(circle at 88% 12%, ${alpha(BRAND.gold, 0.20)}, transparent 30%),
     linear-gradient(180deg, #F8FFFD 0%, #ECFFFC 42%, #FFF9EC 100%)`,
  fontFamily: BODY_FONT,
  color: BRAND.ink,
  position: "relative",
  overflow: "hidden",
});

const DotsLayer = styled(Box)({
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: 0,
  overflow: "hidden",
});

const Dot = styled(Box, {
  shouldForwardProp: (prop) =>
    !["dotLeft", "dotSize", "dotDelay", "dotDuration", "dotColor"].includes(prop),
})(({ dotLeft, dotSize, dotDelay, dotDuration, dotColor }) => ({
  position: "absolute",
  top: -40,
  left: dotLeft,
  width: dotSize,
  height: dotSize,
  borderRadius: "999px",
  background: dotColor,
  opacity: 0.24,
  filter: "blur(0.5px)",
  animation: `${fall} ${dotDuration}s linear infinite`,
  animationDelay: `${dotDelay}s`,
}));

const PageContent = styled(Box)({
  position: "relative",
  zIndex: 2,
});

const HeroShell = styled(Paper)({
  position: "relative",
  overflow: "hidden",
  borderRadius: "42px",
  color: "#FFFFFF",
  minHeight: "540px",
  background:
    `radial-gradient(circle at 14% 16%, ${alpha(BRAND.turquoiseLight, 0.32)}, transparent 30%),
     radial-gradient(circle at 86% 16%, ${alpha(BRAND.goldLight, 0.26)}, transparent 26%),
     linear-gradient(135deg, #031817 0%, #07312F 48%, #2F2508 100%)`,
  boxShadow: "0 42px 110px rgba(0,80,74,0.26)",
  border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
});

const HeroGlow = styled(Box)({
  position: "absolute",
  borderRadius: "999px",
  filter: "blur(5px)",
  opacity: 0.55,
  animation: `${float} 7s ease-in-out infinite`,
});

const HeroPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.14,
  backgroundImage:
    `linear-gradient(${alpha("#FFFFFF", 0.22)} 1px, transparent 1px),
     linear-gradient(90deg, ${alpha("#FFFFFF", 0.22)} 1px, transparent 1px)`,
  backgroundSize: "34px 34px",
  mixBlendMode: "screen",
});

const GlassCard = styled(Paper)({
  background: alpha("#FFFFFF", 0.86),
  backdropFilter: "blur(22px)",
  border: `1px solid ${alpha("#FFFFFF", 0.74)}`,
  borderRadius: "30px",
  boxShadow: "0 28px 80px rgba(0,80,74,0.12)",
  overflow: "hidden",
});

const DarkCard = styled(Paper)({
  background:
    `radial-gradient(circle at 12% 14%, ${alpha(BRAND.turquoise, 0.20)}, transparent 34%),
     radial-gradient(circle at 92% 18%, ${alpha(BRAND.gold, 0.18)}, transparent 32%),
     linear-gradient(135deg, #041918 0%, #061F1E 55%, #2B2107 100%)`,
  color: "#FFFFFF",
  borderRadius: "30px",
  border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
  boxShadow: "0 30px 90px rgba(0,0,0,0.22)",
  overflow: "hidden",
});

const GradientButton = styled(Button)({
  fontFamily: BODY_FONT,
  fontWeight: 900,
  textTransform: "none",
  borderRadius: "999px",
  padding: "13px 25px",
  background: `linear-gradient(135deg, ${BRAND.turquoise} 0%, ${BRAND.gold} 100%)`,
  color: "#FFFFFF",
  boxShadow: `0 16px 36px ${alpha(BRAND.turquoise, 0.26)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BRAND.turquoiseDark} 0%, #B99325 100%)`,
    transform: "translateY(-2px)",
    boxShadow: `0 20px 44px ${alpha(BRAND.gold, 0.25)}`,
  },
});

const SoftButton = styled(Button)({
  fontFamily: BODY_FONT,
  fontWeight: 850,
  textTransform: "none",
  borderRadius: "999px",
  color: BRAND.turquoiseDark,
  border: `1.5px solid ${alpha(BRAND.turquoise, 0.34)}`,
  background: alpha("#FFFFFF", 0.78),
  "&:hover": {
    background: BRAND.turquoiseSoft,
    borderColor: BRAND.turquoise,
  },
});

const Field = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "18px",
    background: alpha("#FFFFFF", 0.92),
  },
  "& label.Mui-focused": {
    color: BRAND.turquoiseDark,
  },
});

const FallbackVisual = styled(Box)({
  position: "relative",
  minHeight: 245,
  borderRadius: "28px",
  overflow: "hidden",
  background:
    `radial-gradient(circle at 18% 22%, ${alpha(BRAND.turquoiseLight, 0.75)}, transparent 30%),
     radial-gradient(circle at 82% 26%, ${alpha(BRAND.goldLight, 0.70)}, transparent 26%),
     linear-gradient(135deg, #06201E 0%, #09433F 58%, #6B5412 100%)`,
  border: `1px solid ${alpha("#FFFFFF", 0.2)}`,
  boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      `linear-gradient(${alpha("#FFFFFF", 0.20)} 1px, transparent 1px),
       linear-gradient(90deg, ${alpha("#FFFFFF", 0.20)} 1px, transparent 1px)`,
    backgroundSize: "30px 30px",
    opacity: 0.28,
  },
  "&:after": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "42%",
    left: "-50%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
    animation: `${shimmer} 5s ease-in-out infinite`,
  },
});

const OpportunityImage = styled(Box, {
  shouldForwardProp: (prop) => prop !== "image",
})(({ image }) => ({
  height: 220,
  position: "relative",
  overflow: "hidden",
  backgroundImage: image ? `url(${image})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundColor: BRAND.deep,
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(3,24,23,0.74) 100%)",
  },
}));

const FooterWrap = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
  borderRadius: "34px 34px 0 0",
  background:
    `radial-gradient(circle at 18% 20%, ${alpha(BRAND.turquoise, 0.22)}, transparent 30%),
     radial-gradient(circle at 82% 20%, ${alpha(BRAND.gold, 0.18)}, transparent 30%),
     linear-gradient(135deg, #061414 0%, #111B1A 58%, #171103 100%)`,
  color: "#FFFFFF",
  borderTop: `1px solid ${alpha("#FFFFFF", 0.16)}`,
  boxShadow: "0 -20px 70px rgba(0,0,0,0.12)",
}));

function BackgroundDots() {
  const dots = useMemo(() => {
    const colours = [
      alpha(BRAND.turquoise, 0.8),
      alpha(BRAND.turquoiseLight, 0.75),
      alpha(BRAND.gold, 0.72),
      alpha(BRAND.goldLight, 0.74),
    ];

    return Array.from({ length: 54 }).map((_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      size: `${Math.floor(Math.random() * 8) + 4}px`,
      delay: Math.random() * 12,
      duration: Math.random() * 12 + 16,
      color: colours[index % colours.length],
    }));
  }, []);

  return (
    <DotsLayer>
      {dots.map((dot) => (
        <Dot
          key={dot.id}
          dotLeft={dot.left}
          dotSize={dot.size}
          dotDelay={dot.delay}
          dotDuration={dot.duration}
          dotColor={dot.color}
        />
      ))}
    </DotsLayer>
  );
}

function ImageBlock({ src, alt, height = 260, children, dark = false }) {
  const safeSrc = forceHttps(src);

  if (!safeSrc) {
    return (
      <FallbackVisual sx={{ minHeight: height }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            textAlign: "center",
            color: "#FFFFFF",
          }}
        >
          <Stack spacing={1.2} alignItems="center">
            <PaletteOutlinedIcon sx={{ fontSize: 58, color: BRAND.goldLight }} />
            {children || (
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: "1.35rem",
                  lineHeight: 1.12,
                }}
              >
                {alt || "Artist Opportunities"}
              </Typography>
            )}
          </Stack>
        </Box>
      </FallbackVisual>
    );
  }

  return (
    <Box
      sx={{
        height,
        position: "relative",
        borderRadius: "28px",
        overflow: "hidden",
        backgroundColor: dark ? BRAND.deep : BRAND.subtle,
        boxShadow: dark
          ? "0 24px 60px rgba(0,0,0,0.22)"
          : "0 18px 45px rgba(0,80,74,0.10)",
      }}
    >
      <Box
        component="img"
        src={safeSrc}
        alt={alt || "Artist opportunities image"}
        loading="lazy"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          transform: "scale(1.01)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            dark
              ? "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.62) 100%)"
              : "linear-gradient(180deg, rgba(0,0,0,0.00) 0%, rgba(0,0,0,0.30) 100%)",
        }}
      />

      {children && (
        <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function SourceButton({ item, label = "Open opportunity" }) {
  return (
    <Button
      href={item.url}
      target="_blank"
      rel="noreferrer"
      endIcon={<LaunchRoundedIcon />}
      sx={{
        textTransform: "none",
        fontWeight: 900,
        color: BRAND.turquoiseDark,
      }}
    >
      {label}
    </Button>
  );
}

function OpportunityCard({ item, featured = false, onSave, saved }) {
  const deadlineLabel = getDeadlineLabel(item);
  const deadlineDays = daysUntil(item.deadlineDate);
  const isUrgent = deadlineDays !== null && deadlineDays >= 0 && deadlineDays <= 14;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.45 }}
      whileHover={{ y: -7 }}
      sx={{ height: "100%" }}
    >
      <Card
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: featured ? 5 : 4,
          overflow: "hidden",
          border: featured
            ? `1.5px solid ${alpha(BRAND.gold, 0.42)}`
            : `1px solid ${alpha(BRAND.turquoise, 0.15)}`,
          background: "#FFFFFF",
          boxShadow: featured
            ? "0 30px 84px rgba(0,80,74,0.18)"
            : "0 18px 54px rgba(0,80,74,0.10)",
        }}
      >
        <OpportunityImage image={forceHttps(item.image || DEFAULT_IMAGE)}>
          <Box
            sx={{
              position: "absolute",
              left: 16,
              right: 16,
              bottom: 16,
              zIndex: 2,
            }}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                icon={getTypeIcon(item.type)}
                label={item.type}
                sx={{
                  background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold})`,
                  color: "#FFFFFF",
                  fontWeight: 950,
                  "& .MuiChip-icon": { color: "#FFFFFF" },
                }}
              />

              {isUrgent && (
                <Chip
                  size="small"
                  icon={<AccessTimeRoundedIcon />}
                  label="Act soon"
                  sx={{
                    backgroundColor: alpha("#FFFFFF", 0.92),
                    color: "#7A2D00",
                    fontWeight: 950,
                    "& .MuiChip-icon": { color: "#7A2D00" },
                  }}
                />
              )}
            </Stack>
          </Box>
        </OpportunityImage>

        <CardContent sx={{ p: featured ? { xs: 3, md: 3.5 } : 2.5, flex: 1 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              icon={<PublicOutlinedIcon />}
              label={normaliseArray(item.countryTags).map(getCountryShort).join(" + ")}
              sx={{
                backgroundColor: BRAND.turquoiseSoft,
                color: BRAND.turquoiseDark,
                fontWeight: 850,
                "& .MuiChip-icon": { color: BRAND.turquoiseDark },
              }}
            />

            <Chip
              size="small"
              icon={<CalendarMonthRoundedIcon />}
              label={deadlineLabel}
              sx={{
                backgroundColor: isUrgent ? alpha("#FF7A00", 0.14) : alpha(BRAND.gold, 0.13),
                color: isUrgent ? "#7A2D00" : "#7A5B00",
                fontWeight: 850,
                "& .MuiChip-icon": { color: isUrgent ? "#7A2D00" : "#7A5B00" },
              }}
            />
          </Stack>

          <Typography
            sx={{
              color: BRAND.ink,
              fontFamily: HEADING_FONT,
              fontWeight: 950,
              lineHeight: 1.1,
              letterSpacing: "-0.045em",
              fontSize: featured ? { xs: "1.75rem", md: "2.3rem" } : "1.25rem",
              mb: 1,
            }}
          >
            {item.title}
          </Typography>

          <Typography
            sx={{
              color: BRAND.turquoiseDark,
              fontWeight: 900,
              mb: 1.2,
            }}
          >
            {item.source}
          </Typography>

          <Typography
            sx={{
              color: BRAND.muted,
              lineHeight: 1.75,
              mb: 2,
            }}
          >
            {item.bestFor}
          </Typography>

          <Grid container spacing={1.2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  background: alpha(BRAND.turquoise, 0.06),
                  border: `1px solid ${alpha(BRAND.turquoise, 0.12)}`,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontWeight: 950, fontSize: "0.84rem" }}>
                  Funding / value
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: "0.88rem" }}>
                  {item.amount}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  background: alpha(BRAND.gold, 0.08),
                  border: `1px solid ${alpha(BRAND.gold, 0.13)}`,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontWeight: 950, fontSize: "0.84rem" }}>
                  Best audience
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: "0.88rem" }}>
                  {item.audience}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              p: 1.6,
              borderRadius: 3,
              background: "#FBFEFD",
              border: `1px solid ${BRAND.border}`,
              mb: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TipsAndUpdatesRoundedIcon sx={{ color: BRAND.turquoiseDark, mt: "2px" }} />
              <Typography sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                <strong style={{ color: BRAND.ink }}>Application tip:</strong>{" "}
                {item.applicationTip}
              </Typography>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: "auto" }}>
            <SourceButton item={item} />
            <SoftButton
              size="small"
              startIcon={saved ? <CheckCircleRoundedIcon /> : <FavoriteRoundedIcon />}
              onClick={() => onSave(item)}
            >
              {saved ? "Saved" : "Save"}
            </SoftButton>
          </Stack>
        </CardContent>
      </Card>
    </MotionBox>
  );
}

function SavedOpportunityItem({ item, onRemove }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.6,
        borderRadius: 4,
        background: alpha("#FFFFFF", 0.78),
        border: `1px solid ${alpha(BRAND.turquoise, 0.16)}`,
      }}
    >
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 950, lineHeight: 1.25 }}>
          {item.title}
        </Typography>

        <Typography sx={{ color: BRAND.muted, fontSize: "0.88rem" }}>
          {item.source} · {item.type}
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button
            href={item.url}
            target="_blank"
            rel="noreferrer"
            size="small"
            endIcon={<LaunchRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 850,
              color: BRAND.turquoiseDark,
            }}
          >
            Open
          </Button>

          <Button
            size="small"
            onClick={() => onRemove(item.id)}
            sx={{
              textTransform: "none",
              fontWeight: 850,
              color: BRAND.muted,
            }}
          >
            Remove
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ActionKitCard({ icon, title, text, bullets }) {
  return (
    <GlassCard sx={{ p: 3, height: "100%" }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "19px",
            display: "grid",
            placeItems: "center",
            color: BRAND.turquoiseDark,
            background: `linear-gradient(135deg, ${alpha(BRAND.turquoise, 0.16)}, ${alpha(BRAND.gold, 0.16)})`,
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontWeight: 950,
            fontSize: "1.25rem",
            letterSpacing: "-0.035em",
          }}
        >
          {title}
        </Typography>

        <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
          {text}
        </Typography>

        <Stack spacing={1}>
          {bullets.map((bullet) => (
            <Stack key={bullet} direction="row" spacing={1} alignItems="flex-start">
              <CheckCircleRoundedIcon
                sx={{ color: BRAND.turquoiseDark, fontSize: 20, mt: "2px" }}
              />
              <Typography sx={{ color: BRAND.muted, lineHeight: 1.65 }}>
                {bullet}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </GlassCard>
  );
}

function OpportunityStats({ total, liveCount, countryLabel, savedCount }) {
  const stats = [
    {
      icon: <PublicOutlinedIcon />,
      value: countryLabel,
      label: "Selected location",
    },
    {
      icon: <LibraryBooksRoundedIcon />,
      value: total,
      label: "Useful routes shown",
    },
    {
      icon: <SyncRoundedIcon />,
      value: liveCount,
      label: "Live backend items",
    },
    {
      icon: <FavoriteRoundedIcon />,
      value: savedCount,
      label: "Saved by you",
    },
  ];

  return (
    <Grid container spacing={1.5}>
      {stats.map((stat) => (
        <Grid item xs={12} sm={6} key={stat.label}>
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              background: alpha("#FFFFFF", 0.12),
              border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
              backdropFilter: "blur(14px)",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.3} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  color: BRAND.ink,
                  background: `linear-gradient(135deg, ${BRAND.turquoiseLight}, ${BRAND.goldLight})`,
                }}
              >
                {stat.icon}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 950, fontSize: "1.08rem" }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ color: alpha("#FFFFFF", 0.66), fontSize: "0.82rem" }}>
                  {stat.label}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

export default function CreativeTechOpportunitiesPage() {
  const [selectedCountry, setSelectedCountry] = useState("GB");
  const [selectedType, setSelectedType] = useState("All");
  const [sortBy, setSortBy] = useState("recommended");
  const [search, setSearch] = useState("");

  const [liveOpportunities, setLiveOpportunities] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedUpdatedAt, setFeedUpdatedAt] = useState(null);
  const [feedMessage, setFeedMessage] = useState("");

  const [savedIds, setSavedIds] = useState([]);
  const [subscription, setSubscription] = useState({
    fullName: "",
    email: "",
    country: "GB",
    practice: "Painting",
    opportunityType: "Grants",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  const selectedCountryLabel = getCountryLabel(selectedCountry);
  const selectedCountryShort = getCountryShort(selectedCountry);

  useEffect(() => {
    const title = `Art Opportunities for ${selectedCountryLabel} | Grants, Open Calls & Residencies | Art For My Room`;
    const description =
      `Find artist opportunities for ${selectedCountryLabel}: grants, open calls, residencies, commissions, public art routes and creative business support for painters and visual artists.`;

    document.title = title;
    updateCanonical(`${SITE_URL}/creative-tech-opportunities`);
    updateMetaTag("description", description);
    updateMetaTag(
      "keywords",
      [
        "artist opportunities",
        "art grants",
        "open calls for artists",
        "artist residencies",
        "public art commissions",
        "painting grants",
        selectedCountryLabel,
        "Art For My Room",
      ].join(", ")
    );
    updateMetaTag("robots", "index, follow, max-image-preview:large");
    updateMetaTag("theme-color", BRAND.turquoise);

    updatePropertyTag("og:site_name", SITE_NAME);
    updatePropertyTag("og:title", title);
    updatePropertyTag("og:description", description);
    updatePropertyTag("og:type", "website");
    updatePropertyTag("og:url", `${SITE_URL}/creative-tech-opportunities`);
    updatePropertyTag("og:image", DEFAULT_IMAGE);

    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", DEFAULT_IMAGE);
  }, [selectedCountryLabel]);

  useEffect(() => {
    let ignore = false;

    async function loadOpportunities() {
      setFeedLoading(true);
      setFeedMessage("");

      try {
        const params = new URLSearchParams();

        params.set("country", selectedCountry);
        params.set("type", selectedType);
        params.set("fresh", "1");

        const response = await fetch(`${OPPORTUNITIES_ENDPOINT}?${params.toString()}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        const contentType = response.headers.get("content-type") || "";

        let data = null;

        if (contentType.includes("application/json")) {
          data = await response.json();
        } else {
          const text = await response.text();
          data = { message: text || "Unexpected opportunities feed response." };
        }

        if (!response.ok) {
          throw new Error(data.message || `Opportunities feed failed: ${response.status}`);
        }

        const rawItems = [
          ...(Array.isArray(data.opportunities) ? data.opportunities : []),
          ...(Array.isArray(data.results) ? data.results : []),
          ...(Array.isArray(data.items) ? data.items : []),
        ];

        const normalised = rawItems
          .map((item, index) => normaliseOpportunity(item, index))
          .filter((item) => item.title && item.url);

        if (!ignore) {
          setLiveOpportunities(dedupeOpportunities(normalised));
          setFeedUpdatedAt(data.updated_at || data.updatedAt || data.generated_at || null);
          setFeedMessage(
            normalised.length
              ? ""
              : "The live opportunities feed returned no items for this filter, so curated trusted sources are shown."
          );
        }
      } catch (error) {
        if (!ignore) {
          setLiveOpportunities([]);
          setFeedUpdatedAt(null);
          setFeedMessage(
            "Live opportunities feed is not available right now. Showing curated trusted sources instead."
          );
        }
      } finally {
        if (!ignore) {
          setFeedLoading(false);
        }
      }
    }

    loadOpportunities();

    const intervalId = window.setInterval(loadOpportunities, 20 * 60 * 1000);

    return () => {
      ignore = true;
      window.clearInterval(intervalId);
    };
  }, [selectedCountry, selectedType]);

  const curatedForCountry = useMemo(() => {
    return curatedOpportunitySources
      .filter((item) => countryMatches(item, selectedCountry))
      .filter((item) => typeMatches(item, selectedType))
      .filter((item) => searchMatches(item, search));
  }, [selectedCountry, selectedType, search]);

  const liveForFilters = useMemo(() => {
    return liveOpportunities
      .filter((item) => countryMatches(item, selectedCountry))
      .filter((item) => typeMatches(item, selectedType))
      .filter((item) => searchMatches(item, search));
  }, [liveOpportunities, selectedCountry, selectedType, search]);

  const combinedOpportunities = useMemo(() => {
    const combined = dedupeOpportunities([...liveForFilters, ...curatedForCountry]);

    return sortOpportunities(combined, sortBy);
  }, [liveForFilters, curatedForCountry, sortBy]);

  const featuredOpportunity = combinedOpportunities[0] || curatedOpportunitySources[0];

  const savedOpportunities = useMemo(() => {
    return combinedOpportunities.filter((item) => savedIds.includes(item.id));
  }, [combinedOpportunities, savedIds]);

  useEffect(() => {
    updateJsonLd(selectedCountryLabel, combinedOpportunities);
  }, [selectedCountryLabel, combinedOpportunities]);

  const urgentCount = combinedOpportunities.filter((item) => {
    const days = daysUntil(item.deadlineDate);
    return days !== null && days >= 0 && days <= 14;
  }).length;

  const handleSave = (item) => {
    setSavedIds((prev) => {
      if (prev.includes(item.id)) {
        return prev.filter((id) => id !== item.id);
      }

      return [...prev, item.id];
    });
  };

  const handleRemoveSaved = (id) => {
    setSavedIds((prev) => prev.filter((item) => item !== id));
  };

  const resetFilters = () => {
    setSelectedCountry("GB");
    setSelectedType("All");
    setSortBy("recommended");
    setSearch("");
  };

  const handleSubscriptionChange = (field) => (event) => {
    setSubscription((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubscribe = async (event) => {
    event.preventDefault();

    const email = subscription.email.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      setSubmitMessage({
        type: "error",
        text: "Please enter a valid email address.",
      });
      return;
    }

    setSubmitting(true);
    setSubmitMessage({ type: "", text: "" });

    try {
      const response = await fetch(SUBSCRIPTION_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          full_name: subscription.fullName.trim(),
          email,
          country: subscription.country,
          country_label: getCountryLabel(subscription.country),
          practice: subscription.practice,
          opportunity_type: subscription.opportunityType,
          notes: subscription.notes.trim(),
          source_page: "CreativeTechOpportunitiesPage",
        }),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.message ||
            data.error ||
            `Subscription failed with status ${response.status}`
        );
      }

      setSubmitMessage({
        type: "success",
        text:
          data.message ||
          "Saved. We will send artist opportunities that match your country and practice.",
      });

      setSubscription((prev) => ({
        ...prev,
        fullName: "",
        email: "",
        notes: "",
      }));
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text:
          error.message ||
          "Could not save your subscription right now. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <BackgroundDots />

      <PageContent>
        <Container
          maxWidth="xl"
          sx={{
            pt: { xs: 3, md: 6 },
            pb: { xs: 5, md: 8 },
          }}
        >
          <HeroShell elevation={0}>
            <HeroPattern />

            <HeroGlow
              sx={{
                width: 260,
                height: 260,
                top: -72,
                left: -80,
                background: alpha(BRAND.turquoiseLight, 0.55),
              }}
            />

            <HeroGlow
              sx={{
                width: 220,
                height: 220,
                top: 86,
                right: -52,
                background: alpha(BRAND.goldLight, 0.42),
                animationDelay: "1.2s",
              }}
            />

            <HeroGlow
              sx={{
                width: 150,
                height: 150,
                bottom: 38,
                left: "43%",
                background: alpha(BRAND.turquoise, 0.32),
                animationDelay: "2.1s",
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                p: { xs: 3, sm: 4, md: 6 },
                minHeight: "540px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <MotionBox
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.65 }}
                  >
                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label="Artist opportunities radar"
                      sx={{
                        background: `linear-gradient(135deg, ${BRAND.turquoiseLight}, ${BRAND.goldLight})`,
                        color: BRAND.ink,
                        fontWeight: 950,
                        fontFamily: MONO_FONT,
                        mb: 2.5,
                        px: 1,
                        "& .MuiChip-icon": {
                          color: BRAND.ink,
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: {
                          xs: "2.45rem",
                          sm: "3.25rem",
                          md: "5.05rem",
                        },
                        letterSpacing: "-0.07em",
                        lineHeight: 0.96,
                        maxWidth: 940,
                      }}
                    >
                      Find the doors that open for artists
                    </Typography>

                    <Typography
                      sx={{
                        color: alpha("#FFFFFF", 0.8),
                        maxWidth: 780,
                        mt: 2.4,
                        lineHeight: 1.9,
                        fontSize: {
                          xs: "1rem",
                          md: "1.15rem",
                        },
                      }}
                    >
                      Grants, open calls, residencies, commissions, travel routes and
                      creative business support, curated for painters and visual artists.
                      Select a country and let the opportunity map rearrange itself.
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ mt: 3 }}
                    >
                      <GradientButton
                        startIcon={<SearchRoundedIcon />}
                        onClick={() => {
                          document.getElementById("opportunity-filters")?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }}
                      >
                        Search opportunities
                      </GradientButton>

                      <SoftButton
                        startIcon={<EmailOutlinedIcon />}
                        onClick={() => {
                          document.getElementById("opportunity-email")?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }}
                        sx={{
                          color: "#FFFFFF",
                          borderColor: alpha("#FFFFFF", 0.38),
                          background: alpha("#FFFFFF", 0.09),
                          "&:hover": {
                            background: alpha("#FFFFFF", 0.14),
                            borderColor: "#FFFFFF",
                          },
                        }}
                      >
                        Get artist alerts
                      </SoftButton>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 3 }}
                    >
                      {[
                        "Funding",
                        "Residencies",
                        "Public art",
                        "Open calls",
                        "Creative business",
                      ].map((item) => (
                        <Chip
                          key={item}
                          label={item}
                          sx={{
                            bgcolor: alpha("#FFFFFF", 0.1),
                            color: "#FFFFFF",
                            border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
                            fontWeight: 850,
                          }}
                        />
                      ))}
                    </Stack>
                  </MotionBox>
                </Grid>

                <Grid item xs={12} md={5}>
                  <MotionBox
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.65, delay: 0.1 }}
                  >
                    <DarkCard sx={{ p: 3 }}>
                      <Stack spacing={2.2}>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 950,
                              fontSize: "1.75rem",
                              letterSpacing: "-0.045em",
                              mb: 1,
                            }}
                          >
                            Today’s opportunity compass
                          </Typography>

                          <Typography sx={{ color: alpha("#FFFFFF", 0.7), lineHeight: 1.8 }}>
                            Built for artists who do not want to miss grants, calls,
                            residencies and small openings that can change the next six months.
                          </Typography>
                        </Box>

                        <OpportunityStats
                          total={combinedOpportunities.length}
                          liveCount={liveForFilters.length}
                          countryLabel={selectedCountryShort}
                          savedCount={savedIds.length}
                        />

                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 4,
                            background: alpha("#FFFFFF", 0.10),
                            border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 3,
                                display: "grid",
                                placeItems: "center",
                                background: `linear-gradient(135deg, ${BRAND.turquoiseLight}, ${BRAND.goldLight})`,
                                color: BRAND.ink,
                              }}
                            >
                              <AccessTimeRoundedIcon />
                            </Box>

                            <Box>
                              <Typography sx={{ fontWeight: 950 }}>
                                {urgentCount ? `${urgentCount} urgent calls` : "No urgent panic"}
                              </Typography>
                              <Typography sx={{ color: alpha("#FFFFFF", 0.66), fontSize: "0.86rem" }}>
                                Deadlines are signposted where available.
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </DarkCard>
                  </MotionBox>
                </Grid>
              </Grid>
            </Box>
          </HeroShell>

          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} md={3}>
              <MotionBox
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <GlassCard sx={{ p: 3, height: "100%" }}>
                  <PaidOutlinedIcon sx={{ color: BRAND.turquoiseDark, fontSize: 38, mb: 1 }} />
                  <Typography sx={{ fontWeight: 950, fontSize: "1.15rem", mb: 1 }}>
                    Funding routes
                  </Typography>
                  <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                    Official grants and funding portals, with the strongest UK routes at the front.
                  </Typography>
                </GlassCard>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={3}>
              <MotionBox
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 }}
                whileHover={{ y: -5 }}
              >
                <GlassCard sx={{ p: 3, height: "100%" }}>
                  <HomeWorkOutlinedIcon sx={{ color: BRAND.turquoiseDark, fontSize: 38, mb: 1 }} />
                  <Typography sx={{ fontWeight: 950, fontSize: "1.15rem", mb: 1 }}>
                    Residencies
                  </Typography>
                  <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                    Studio time, travel, hosts, exchange and concentrated making time.
                  </Typography>
                </GlassCard>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={3}>
              <MotionBox
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
              >
                <GlassCard sx={{ p: 3, height: "100%" }}>
                  <BrushOutlinedIcon sx={{ color: BRAND.turquoiseDark, fontSize: 38, mb: 1 }} />
                  <Typography sx={{ fontWeight: 950, fontSize: "1.15rem", mb: 1 }}>
                    Commissions
                  </Typography>
                  <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                    Public art, exhibitions, proposals and calls where artists can show work.
                  </Typography>
                </GlassCard>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={3}>
              <MotionBox
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                whileHover={{ y: -5 }}
              >
                <GlassCard sx={{ p: 3, height: "100%" }}>
                  <WorkOutlineRoundedIcon sx={{ color: BRAND.turquoiseDark, fontSize: 38, mb: 1 }} />
                  <Typography sx={{ fontWeight: 950, fontSize: "1.15rem", mb: 1 }}>
                    Creative business
                  </Typography>
                  <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                    Useful for artists building platforms, shops, AI tools or studio businesses.
                  </Typography>
                </GlassCard>
              </MotionBox>
            </Grid>
          </Grid>

          <GlassCard
            id="opportunity-filters"
            sx={{
              mt: 4,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Chip
                    icon={<FilterAltRoundedIcon />}
                    label="Country and opportunity filters"
                    sx={{
                      backgroundColor: BRAND.turquoiseSoft,
                      color: BRAND.turquoiseDark,
                      fontWeight: 950,
                      mb: 1.5,
                      "& .MuiChip-icon": {
                        color: BRAND.turquoiseDark,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: { xs: "1.75rem", md: "2.35rem" },
                      letterSpacing: "-0.045em",
                    }}
                  >
                    Choose where your next chance might open
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <SoftButton
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={resetFilters}
                  >
                    Reset filters
                  </SoftButton>

                  <SoftButton
                    startIcon={<SyncRoundedIcon />}
                    onClick={() => {
                      setSelectedCountry((prev) => prev);
                      setFeedMessage("Refreshing the live feed. Curated sources stay visible.");
                    }}
                  >
                    Refresh
                  </SoftButton>
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Field
                    select
                    fullWidth
                    label="Country"
                    value={selectedCountry}
                    onChange={(event) => {
                      setSelectedCountry(event.target.value);
                      setSubscription((prev) => ({
                        ...prev,
                        country: event.target.value,
                      }));
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PublicOutlinedIcon sx={{ color: BRAND.turquoiseDark }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {countryOptions.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    select
                    fullWidth
                    label="Opportunity type"
                    value={selectedType}
                    onChange={(event) => setSelectedType(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOfferOutlinedIcon sx={{ color: BRAND.turquoiseDark }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {typeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    select
                    fullWidth
                    label="Sort"
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StarRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {sortOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    fullWidth
                    label="Search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="grant, residency, public art..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {feedLoading ? (
                <Box>
                  <LinearProgress
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      backgroundColor: alpha(BRAND.turquoise, 0.12),
                      "& .MuiLinearProgress-bar": {
                        background: `linear-gradient(90deg, ${BRAND.turquoise}, ${BRAND.gold})`,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      mt: 1,
                      color: BRAND.muted,
                      fontFamily: MONO_FONT,
                      fontSize: "0.86rem",
                    }}
                  >
                    Checking the live opportunities feed...
                  </Typography>
                </Box>
              ) : (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    icon={<LibraryBooksRoundedIcon />}
                    label={`${combinedOpportunities.length} routes shown`}
                    sx={{
                      backgroundColor: alpha(BRAND.turquoise, 0.10),
                      color: BRAND.turquoiseDark,
                      fontWeight: 900,
                      "& .MuiChip-icon": { color: BRAND.turquoiseDark },
                    }}
                  />

                  <Chip
                    icon={<SyncRoundedIcon />}
                    label={
                      feedUpdatedAt
                        ? `Live feed updated ${formatDateTime(feedUpdatedAt)}`
                        : "Curated sources active"
                    }
                    sx={{
                      backgroundColor: alpha(BRAND.gold, 0.13),
                      color: "#7A5B00",
                      fontWeight: 900,
                      "& .MuiChip-icon": { color: "#7A5B00" },
                    }}
                  />

                  {feedMessage && (
                    <Chip
                      label={feedMessage}
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        py: 0.8,
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          lineHeight: 1.5,
                        },
                      }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </GlassCard>

          {featuredOpportunity && (
            <Box sx={{ mt: { xs: 5, md: 7 } }}>
              <Grid container spacing={4} alignItems="stretch">
                <Grid item xs={12} lg={7}>
                  <OpportunityCard
                    item={featuredOpportunity}
                    featured
                    saved={savedIds.includes(featuredOpportunity.id)}
                    onSave={handleSave}
                  />
                </Grid>

                <Grid item xs={12} lg={5}>
                  <DarkCard sx={{ p: { xs: 3, md: 4 }, height: "100%" }}>
                    <Stack spacing={2.5}>
                      <Chip
                        icon={<AutoAwesomeIcon />}
                        label={`Opportunity strategy for ${selectedCountryLabel}`}
                        sx={{
                          alignSelf: "flex-start",
                          backgroundColor: alpha("#FFFFFF", 0.12),
                          color: "#FFFFFF",
                          fontWeight: 950,
                          border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
                          "& .MuiChip-icon": {
                            color: BRAND.goldLight,
                          },
                        }}
                      />

                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 950,
                          fontSize: { xs: "2rem", md: "2.7rem" },
                          letterSpacing: "-0.055em",
                          lineHeight: 1.04,
                        }}
                      >
                        Apply less randomly. Apply where the painting has a real reason to be.
                      </Typography>

                      <Typography sx={{ color: alpha("#FFFFFF", 0.74), lineHeight: 1.85 }}>
                        The strongest artist applications usually connect four things:
                        the work, the place, the audience and the next step in the artist’s practice.
                        This page is designed to help painters find that bridge faster.
                      </Typography>

                      <Stack spacing={1.4}>
                        {[
                          "Check eligibility before writing anything.",
                          "Use clear project language, not only poetic language.",
                          "Prepare images, CV, budget and timeline in advance.",
                          "Track deadlines weekly so opportunities do not vanish in the fog.",
                        ].map((item) => (
                          <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                            <VerifiedRoundedIcon
                              sx={{ color: BRAND.goldLight, mt: "2px" }}
                            />
                            <Typography sx={{ color: alpha("#FFFFFF", 0.78), lineHeight: 1.7 }}>
                              {item}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  </DarkCard>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: { xs: 5, md: 7 } }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: { xs: "2.05rem", md: "3.05rem" },
                  letterSpacing: "-0.055em",
                  mb: 1,
                }}
              >
                Opportunities for {selectedCountryLabel}
              </Typography>

              <Typography
                sx={{
                  color: BRAND.muted,
                  maxWidth: 760,
                  mx: "auto",
                  lineHeight: 1.8,
                }}
              >
                Live feed items are shown first when available. Trusted sources stay
                underneath so artists always have somewhere useful to go.
              </Typography>
            </Box>

            {combinedOpportunities.length === 0 ? (
              <GlassCard sx={{ p: { xs: 3, md: 5 }, textAlign: "center" }}>
                <SearchRoundedIcon sx={{ fontSize: 56, color: BRAND.turquoiseDark, mb: 1 }} />
                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 950,
                    fontSize: "1.8rem",
                    mb: 1,
                  }}
                >
                  No opportunities found for this filter
                </Typography>
                <Typography sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                  Try a wider country option, choose “All” opportunity types, or search for
                  terms like grant, residency, exhibition or public art.
                </Typography>
              </GlassCard>
            ) : (
              <Grid container spacing={3}>
                {combinedOpportunities.map((item) => (
                  <Grid item xs={12} md={6} lg={4} key={item.id}>
                    <OpportunityCard
                      item={item}
                      saved={savedIds.includes(item.id)}
                      onSave={handleSave}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box sx={{ mt: { xs: 5, md: 7 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <ActionKitCard
                  icon={<AssignmentTurnedInOutlinedIcon />}
                  title="Before applying"
                  text="Do not start with the form. Start with fit."
                  bullets={[
                    "Confirm location and eligibility.",
                    "Check if individuals, collectives or organisations can apply.",
                    "Read assessment criteria before drafting.",
                    "Save deadline, budget limit and required documents.",
                  ]}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ActionKitCard
                  icon={<BrushOutlinedIcon />}
                  title="Artist material kit"
                  text="A prepared artist looks more serious before the application even breathes."
                  bullets={[
                    "10 strong images of recent work.",
                    "Short artist statement and longer project statement.",
                    "CV or biography with exhibitions and education.",
                    "Simple budget, timeline and intended outcome.",
                  ]}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ActionKitCard
                  icon={<FavoriteRoundedIcon />}
                  title="For painters selling online"
                  text="Use opportunities to build trust, visibility and buyer confidence."
                  bullets={[
                    "Apply to calls that can lead to exhibitions or press.",
                    "Use residencies to create a new body of work.",
                    "Use grants to improve presentation and documentation.",
                    "Link finished projects back to your painting listings.",
                  ]}
                />
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={4} sx={{ mt: { xs: 5, md: 7 } }}>
            <Grid item xs={12} lg={7}>
              <DarkCard
                id="opportunity-email"
                sx={{ p: { xs: 3, md: 4 }, height: "100%" }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Chip
                      icon={<EmailOutlinedIcon />}
                      label="Artist opportunity alerts"
                      sx={{
                        backgroundColor: alpha("#FFFFFF", 0.12),
                        color: "#FFFFFF",
                        fontWeight: 950,
                        border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
                        mb: 2,
                        "& .MuiChip-icon": { color: BRAND.goldLight },
                      }}
                    />

                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: { xs: "2rem", md: "2.7rem" },
                        letterSpacing: "-0.055em",
                        lineHeight: 1.04,
                        mb: 1.5,
                      }}
                    >
                      Let useful calls arrive before the deadline monsters wake up.
                    </Typography>

                    <Typography sx={{ color: alpha("#FFFFFF", 0.72), lineHeight: 1.85 }}>
                      Artists can receive curated opportunities by country, practice and
                      opportunity type. Simple, useful, location-aware.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      component="form"
                      onSubmit={handleSubscribe}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 5,
                        background: alpha("#FFFFFF", 0.9),
                        border: `1px solid ${alpha("#FFFFFF", 0.3)}`,
                      }}
                    >
                      <Stack spacing={2}>
                        <Field
                          fullWidth
                          label="Full name"
                          value={subscription.fullName}
                          onChange={handleSubscriptionChange("fullName")}
                        />

                        <Field
                          fullWidth
                          required
                          type="email"
                          label="Email"
                          value={subscription.email}
                          onChange={handleSubscriptionChange("email")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlinedIcon sx={{ color: BRAND.turquoiseDark }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              select
                              fullWidth
                              label="Country"
                              value={subscription.country}
                              onChange={handleSubscriptionChange("country")}
                            >
                              {countryOptions.map((country) => (
                                <MenuItem key={country.value} value={country.value}>
                                  {country.label}
                                </MenuItem>
                              ))}
                            </Field>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              select
                              fullWidth
                              label="Practice"
                              value={subscription.practice}
                              onChange={handleSubscriptionChange("practice")}
                            >
                              {practiceOptions.map((practice) => (
                                <MenuItem key={practice} value={practice}>
                                  {practice}
                                </MenuItem>
                              ))}
                            </Field>
                          </Grid>
                        </Grid>

                        <Field
                          select
                          fullWidth
                          label="Main opportunity type"
                          value={subscription.opportunityType}
                          onChange={handleSubscriptionChange("opportunityType")}
                        >
                          {typeOptions.filter((type) => type !== "All").map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Field>

                        <Field
                          fullWidth
                          multiline
                          minRows={3}
                          label="Notes"
                          value={subscription.notes}
                          onChange={handleSubscriptionChange("notes")}
                          placeholder="Example: I am a painter in London looking for grants and open calls."
                        />

                        {submitMessage.text && (
                          <Alert
                            severity={submitMessage.type}
                            sx={{ borderRadius: 3 }}
                          >
                            {submitMessage.text}
                          </Alert>
                        )}

                        <GradientButton
                          type="submit"
                          disabled={submitting}
                          startIcon={
                            submitting ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <EmailOutlinedIcon />
                            )
                          }
                        >
                          {submitting ? "Saving..." : "Send me opportunities"}
                        </GradientButton>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </DarkCard>
            </Grid>

            <Grid item xs={12} lg={5}>
              <GlassCard sx={{ p: { xs: 3, md: 4 }, height: "100%" }}>
                <Stack spacing={2.2}>
                  <Chip
                    icon={<FavoriteRoundedIcon />}
                    label="Saved opportunities"
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: BRAND.turquoiseSoft,
                      color: BRAND.turquoiseDark,
                      fontWeight: 950,
                      "& .MuiChip-icon": { color: BRAND.turquoiseDark },
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: { xs: "1.75rem", md: "2.35rem" },
                      letterSpacing: "-0.045em",
                    }}
                  >
                    Your shortlist
                  </Typography>

                  <Typography sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                    Save useful routes while browsing. This shortlist is temporary in the
                    browser, a neat little basket for today’s research.
                  </Typography>

                  {savedOpportunities.length ? (
                    <Stack spacing={1.4}>
                      {savedOpportunities.map((item) => (
                        <SavedOpportunityItem
                          key={item.id}
                          item={item}
                          onRemove={handleRemoveSaved}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 4,
                        background: alpha(BRAND.turquoise, 0.06),
                        border: `1px dashed ${alpha(BRAND.turquoise, 0.34)}`,
                        textAlign: "center",
                      }}
                    >
                      <FavoriteRoundedIcon
                        sx={{ color: BRAND.turquoiseDark, fontSize: 42, mb: 1 }}
                      />
                      <Typography sx={{ fontWeight: 950, mb: 0.5 }}>
                        No saved opportunities yet
                      </Typography>
                      <Typography sx={{ color: BRAND.muted }}>
                        Tap “Save” on any opportunity card.
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </GlassCard>
            </Grid>
          </Grid>
        </Container>

        <FooterWrap>
          <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
            <Grid container spacing={4} alignItems="flex-start">
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold})`,
                      boxShadow: `0 14px 34px ${alpha(BRAND.turquoise, 0.22)}`,
                    }}
                  >
                    <PaletteOutlinedIcon sx={{ color: "#FFFFFF" }} />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: "1.28rem",
                        letterSpacing: "-0.03em",
                      }}
                    >
                      Art For My Room
                    </Typography>

                    <Typography sx={{ color: alpha("#FFFFFF", 0.62), fontSize: 14 }}>
                      Artist opportunities, real rooms and original paintings.
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  sx={{
                    color: alpha("#FFFFFF", 0.72),
                    lineHeight: 1.8,
                    maxWidth: 560,
                  }}
                >
                  A UK-built platform helping painters list original works, match paintings
                  with real rooms and discover opportunities that support creative growth.
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontWeight: 950, mb: 1.5 }}>
                  Explore
                </Typography>

                <Stack spacing={1}>
                  {[
                    { label: "Home", href: "/" },
                    { label: "Find Painting", href: "/find-painting" },
                    { label: "Upload Painting", href: "/artist-launch" },
                    { label: "Creator Radar", href: "/creator-radar" },
                  ].map((link) => (
                    <Box
                      key={link.label}
                      component="a"
                      href={link.href}
                      sx={{
                        color: alpha("#FFFFFF", 0.72),
                        textDecoration: "none",
                        fontWeight: 650,
                        width: "fit-content",
                        "&:hover": {
                          color: BRAND.goldLight,
                        },
                      }}
                    >
                      {link.label}
                    </Box>
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontWeight: 950, mb: 1.5 }}>
                  Opportunity radar includes
                </Typography>

                <Stack spacing={1.2}>
                  {[
                    "UK arts grants and funding routes",
                    "International residencies and open calls",
                    "Public art and commission discovery",
                    "Creative business and innovation support",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1} alignItems="center">
                      <VerifiedRoundedIcon sx={{ color: BRAND.goldLight, fontSize: 20 }} />
                      <Typography sx={{ color: alpha("#FFFFFF", 0.72), fontWeight: 650 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Box
                  component="a"
                  href={`mailto:${SITE_EMAIL}`}
                  sx={{
                    mt: 2.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#FFFFFF",
                    textDecoration: "none",
                    fontWeight: 900,
                    px: 2,
                    py: 1.2,
                    borderRadius: 999,
                    background: alpha("#FFFFFF", 0.1),
                    border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
                    "&:hover": {
                      background: alpha("#FFFFFF", 0.16),
                      color: BRAND.goldLight,
                    },
                  }}
                >
                  <EmailOutlinedIcon fontSize="small" />
                  {SITE_EMAIL}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3, borderColor: alpha("#FFFFFF", 0.14) }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Typography sx={{ color: alpha("#FFFFFF", 0.58), fontSize: 14 }}>
                © {new Date().getFullYear()} Art For My Room. All rights reserved.
              </Typography>

              <Typography sx={{ color: alpha("#FFFFFF", 0.58), fontSize: 14 }}>
                Opportunities worldwide. Painters worldwide. Rooms worldwide.
              </Typography>
            </Stack>
          </Container>
        </FooterWrap>
      </PageContent>
    </PageShell>
  );
}
